import { createRouter, createWebHistory } from 'vue-router'
import { getSession } from '../services/auth'
import { getMe, getProgress, checkAdmin } from '../services/api'
import Login from '../components/Login.vue'
import Callback from '../components/Callback.vue'
import WorkshopStatusView from '../views/WorkshopStatusView.vue'
import SlideView from '../views/SlideView.vue'
import AdminView from '../views/AdminView.vue'
import AdminUsersView from '../views/AdminUsersView.vue'
import AdminCollegesView from '../views/AdminCollegesView.vue'
import AdminGuidesView from '../views/AdminGuidesView.vue'
import AdminGuideDocView from '../views/AdminGuideDocView.vue'
import SelectCollegeView from '../views/SelectCollegeView.vue'

function canEnterWorkshop(access) {
  const me = access.me || {}
  return (
    !!access.isAdmin ||
    (access.status === 'approved' &&
      !!access.collegeId &&
      access.workshopState === 'today' &&
      me.sessionEnabled !== false)
  )
}

function hasCollegeAssignment(access) {
  const me = access.me || {}
  // A leftover "please add my college" is not a pick. Next visit is select-college.
  if (me.workshopState === 'requested' || (me.requestedCollegeName && !me.collegeId)) {
    return false
  }
  return !!(me.collegeId || me.collegeName)
}

function studentHome(access) {
  if (!hasCollegeAssignment(access) || access.status === 'removed') {
    return '/select-college'
  }
  if (canEnterWorkshop(access)) {
    return null
  }
  return '/workshop-status'
}

async function resolveAccess() {
  const session = await getSession()
  if (!session) {
    return { authenticated: false }
  }

  let me
  try {
    me = await getMe()
  } catch {
    me = {
      status: session.status || 'pending',
      isAdmin: !!session.isAdmin,
      collegeId: null,
      collegeName: null,
      requestedCollegeName: null,
      workshopState: null,
    }
  }

  let isAdmin = !!me.isAdmin
  if (!isAdmin) {
    try {
      const adminRes = await checkAdmin()
      isAdmin = !!adminRes.isAdmin
    } catch {
      /* ignore */
    }
  }

  // "rejected" is the legacy name for "removed" (pre-rename profiles).
  const rawStatus = me.status === 'rejected' ? 'removed' : me.status
  const status = isAdmin ? 'approved' : rawStatus
  return {
    authenticated: true,
    status,
    isAdmin,
    collegeId: me.collegeId || null,
    workshopState: me.workshopState || null,
    me,
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: Login,
      meta: { public: true },
    },
    {
      path: '/callback',
      name: 'callback',
      component: Callback,
      meta: { public: true },
    },
    {
      path: '/select-college',
      name: 'select-college',
      component: SelectCollegeView,
      meta: { requiresAuth: true },
    },
    {
      path: '/workshop-status',
      name: 'workshop-status',
      component: WorkshopStatusView,
      meta: { requiresAuth: true },
    },
    {
      path: '/college-requested',
      redirect: '/login',
    },
    {
      path: '/pending',
      redirect: '/workshop-status',
    },
    {
      path: '/slides/:slideId',
      name: 'slide',
      component: SlideView,
      meta: { requiresAuth: true, requiresWorkshopDay: true, requiresCollege: true },
    },
    {
      path: '/admin',
      name: 'admin',
      component: AdminView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: AdminUsersView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/colleges',
      name: 'admin-colleges',
      component: AdminCollegesView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/guides',
      name: 'admin-guides',
      component: AdminGuidesView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/guides/:doc',
      name: 'admin-guide-doc',
      component: AdminGuideDocView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/',
      name: 'home',
      component: { template: '<div class="muted">Redirecting…</div>' },
      meta: { requiresAuth: true },
      beforeEnter: async (to, from, next) => {
        const access = await resolveAccess()
        if (!access.authenticated) {
          next('/login')
          return
        }
        if (access.isAdmin) {
          try {
            const progress = await getProgress()
            if (progress?.lastSlideId) {
              next(`/slides/${progress.lastSlideId}`)
              return
            }
          } catch {
            /* fall through */
          }
          next('/slides/01')
          return
        }
        const dest = studentHome(access)
        if (dest) {
          next(dest)
          return
        }
        try {
          const progress = await getProgress()
          if (progress?.lastSlideId) {
            next(`/slides/${progress.lastSlideId}`)
            return
          }
        } catch {
          /* fall through */
        }
        next('/slides/01')
      },
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  if (to.meta.public) {
    if (to.name === 'login') {
      const access = await resolveAccess()
      if (access.authenticated) {
        if (access.isAdmin) next('/')
        else next(studentHome(access) || '/')
        return
      }
    }
    next()
    return
  }

  const access = await resolveAccess()

  if (to.meta.requiresAuth && !access.authenticated) {
    next('/login')
    return
  }

  if (access.authenticated && !access.isAdmin) {
    const skipCollegeGate = to.name === 'select-college' || to.name === 'workshop-status'

    if (!hasCollegeAssignment(access) && to.meta.requiresCollege) {
      next('/select-college')
      return
    }

    if (!hasCollegeAssignment(access) && !skipCollegeGate) {
      next('/select-college')
      return
    }

    if (access.status === 'removed' && to.name !== 'select-college') {
      next('/select-college')
      return
    }

    if (
      hasCollegeAssignment(access) &&
      access.status !== 'removed' &&
      !canEnterWorkshop(access) &&
      to.name !== 'workshop-status' &&
      to.name !== 'select-college'
    ) {
      next('/workshop-status')
      return
    }
  }

  if (to.meta.requiresWorkshopDay) {
    if (!canEnterWorkshop(access)) {
      if (!hasCollegeAssignment(access) || access.status === 'removed') next('/select-college')
      else next('/workshop-status')
      return
    }
  }

  if (to.meta.requiresAdmin) {
    if (!access.isAdmin) {
      next('/')
      return
    }
  }

  next()
})

export default router
export { resolveAccess, hasCollegeAssignment, studentHome }
